import {
    Autocomplete,
    Button,
    Divider,
    TextField,
    Portal,
    Typography,
    IconButton
} from '@mui/material';
import {
    createStyles,
    WithStyles,
    withStyles
} from '@mui/styles';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import { CCSpinner } from '../../shared/components/cc-spinner';
import { WorkLogItem } from '../work-log-item';
import { TimeTrackComment, TimeTrackPayload } from '../../models/report/report-timetrack';
import { isValidEmail } from '../../shared/utils';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { CCSingleDatePicker } from '../cc-single-date-picker';

dayjs.extend(utc);
const UTC_FORMAT = 'YYYY-MM-DDTHH:mm:ss[Z]';
const STRIP_LEADING_ZEROS = /\b0+/g;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const styles = (theme: Theme) => createStyles({
    root: {
        display: 'flex',
        flexDirection: 'column'
    },
    textField: {
        flex: 1,
        minHeight: '6em'
    },
    textFieldBlank: {},
    buttonsContainer: {
        '& > *': {
            marginRight: '1.5em'
        }
    },
    logItemContainer: {
        position: 'relative'
    },
    timeTrackerWrapper: {
        background: theme.palette.background.default,
        padding: '1em',
        border: '1px solid #e0e0e0',
    },
    timeTrackerContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        columnGap: '2em',
        '& > div': {
            width: '45%'
        },
    },
    editButton: {
        position: 'absolute',
        top: '0.5em',
        right: '0.5em'
    },
    portalContainer: {
        '&.last-comment': {
            marginBottom: '2em'
        },
        '& > $timeTrackerWrapper': {
            background: theme.ccPalette.cc_colors.translucent.ccCentersilhouette.light,
            border: '1px solid #009933',
            marginTop: '1em'
        },
        '&:has($timeTrackerWrapper) + $editButton': {
            display: 'none'
        }
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        '& svg': {
            marginRight: '0.3em'
        }
    }
});

const DEFAULT_NEW_VALUES = {
    email: '',
    minutesValue: 0,
    hoursValue: 0,
    isEmailInvalid: false
};

const DEFAULT_EDIT_VALUES = {
    editId: '',
    editEmail: '',
    editDate: '',
    editMinutesValue: 0,
    editHoursValue: 0,
    isEditEmailInvalid: false,
    portalRef: null,
    formDate: dayjs() as unknown as Date
};

const DEFAULT_VALUES = {
    ...DEFAULT_NEW_VALUES,
    ...DEFAULT_EDIT_VALUES
};

interface Props extends WithStyles<typeof styles> {
    comments: TimeTrackComment[] | undefined;
    isLoading: boolean;
    isAdmin: boolean;
    emailList: string[];
    updateEmailList: (email: string) => void;
    onTimeTrackPost: (comment: TimeTrackPayload, trackId?: string) => void;
}

interface States {
    email: string;
    minutesValue: number;
    hoursValue: number;
    isEmailInvalid: boolean;
    editId: string;
    editEmail: string;
    editDate: string;
    editMinutesValue: number;
    editHoursValue: number;
    isEditEmailInvalid: boolean;
    portalRef: HTMLElement | null;
    formDate?: Date;
}

class WorkLogEditor extends React.Component<Props, States> {
    public static defaultProps: Partial<Props> = {
    };

    state = DEFAULT_VALUES;
    isComponentMounted = true;

    componentWillUnmount() {
        this.isComponentMounted = false;
    }

    onEmailChanged = (event: React.SyntheticEvent<Element, Event>, value: string, isEdit: boolean) => {
        const { updateEmailList } = this.props;
        const email = value.trim();
        const isEmailValid = isValidEmail(email);

        if (isEdit) {
            this.setState({
                editEmail: email,
                isEditEmailInvalid: !isEmailValid
            }, () => {
                updateEmailList(email);
            });
        } else {
            this.setState({
                email,
                isEmailInvalid: !isEmailValid
            }, () => {
                updateEmailList(email);
            });
        }
    };

    onPostClicked = (isEdit: boolean) => {
        const { onTimeTrackPost } = this.props;
        const {
            email,
            hoursValue,
            minutesValue,
            editId,
            editEmail,
            editHoursValue,
            editMinutesValue,
            editDate
        } = this.state;

        const currentDateUTC = dayjs().utc().format(UTC_FORMAT);
        const body = isEdit ? {
            email: editEmail,
            hours: editHoursValue,
            minutes: editMinutesValue,
            date: editDate !== '' ? editDate : currentDateUTC
        } : {
            email,
            hours: hoursValue,
            minutes: minutesValue,
            date: currentDateUTC
        };

        onTimeTrackPost(body, isEdit ? editId : undefined);
        this.setState(DEFAULT_VALUES);
    };

    onCancelClicked = (isEdit: boolean) => {
        if (isEdit) {
            this.setState(DEFAULT_EDIT_VALUES);
        } else {
            this.setState(DEFAULT_NEW_VALUES);
        }
    };

    onMinutesChanged = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, isEdit: boolean) => {
        const currentValue = event.target.value.replace(STRIP_LEADING_ZEROS, '');
        const minutesValue = currentValue === '' ? 0 : Number.parseInt(currentValue, 10);

        if (minutesValue <= 999) {
            if (isEdit) {
                this.setState({editMinutesValue: minutesValue});
            } else {
                this.setState({minutesValue});
            }
        }
    };

    onHoursChanged = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, isEdit: boolean) => {
        const currentValue = event.target.value.replace(STRIP_LEADING_ZEROS, '');
        const hoursValue = currentValue === '' ? 0 : Number.parseInt(currentValue, 10);

        if (hoursValue <= 999) {
            if (isEdit) {
                this.setState({editHoursValue: hoursValue});
            } else {
                this.setState({hoursValue});
            }
        }
    };

    onDateChange = (date: Date) => {
        this.setState({
            editDate: dayjs(date).utc().format(UTC_FORMAT),
            formDate: date
        });
    };

    renderTimeTrackerForm = (isEdit = false) => {
        const {
            classes,
            isLoading,
            emailList
        } = this.props;

        const{
            minutesValue,
            editMinutesValue,
            hoursValue,
            editHoursValue,
            isEmailInvalid,
            isEditEmailInvalid,
            email: newEmail,
            editEmail,
            formDate
        } = this.state;

        const email = isEdit ? editEmail : newEmail;
        const hours = isEdit ? editHoursValue : hoursValue;
        const minutes = isEdit ? editMinutesValue : minutesValue;
        const isFieldBlank =  (minutes === 0 && hours === 0) || email.length < 1;
        const textFieldClasses = `${classes.textField}${ isFieldBlank ? ` ${classes.textFieldBlank}` : ''}`;
        const isInvalid = isEdit ? isEditEmailInvalid : isEmailInvalid;

        return (
            <div className={classes.timeTrackerWrapper}>
                {
                    !isEdit && (
                        <Typography
                            className={classes.title}
                            variant="subtitle2"
                        >
                            <AddIcon/> Add new work log
                        </Typography>
                    )
                }
                <div className={classes.timeTrackerContainer}>
                    <TextField
                        className={textFieldClasses}
                        value={hours}
                        label="Hours"
                        margin="normal"
                        variant="standard"
                        helperText="Enter from 0 to 999"
                        onChange={(event) => this.onHoursChanged(event, isEdit)}
                    />
                    <TextField
                        className={textFieldClasses}
                        value={minutes}
                        label="Minutes"
                        margin="normal"
                        variant="standard"
                        helperText="Enter from 0 to 999"
                        onChange={(event) => this.onMinutesChanged(event, isEdit)}
                    />
                </div>
                { isEdit &&
                    <CCSingleDatePicker
                        value={formDate}
                        maxDate={dayjs()}
                        label="Date"
                        onDateChange={this.onDateChange}
                    />
                }
                <Autocomplete
                    className={textFieldClasses}
                    disablePortal
                    options={emailList}
                    renderInput={(params) => <TextField error={isInvalid} variant="standard" {...params} label="Performed by:" />}
                    inputValue={email}
                    onInputChange={(event, value) => this.onEmailChanged(event, value, isEdit)}
                />
                { !isFieldBlank && !isInvalid ?
                    <div className={classes.buttonsContainer}>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            disabled={!!isLoading}
                            onClick={() => this.onPostClicked(isEdit)}
                        >
                            { isEdit ? 'SAVE' : 'ADD' }
                        </Button>
                        <Button
                            size="small"
                            onClick={() => this.onCancelClicked(isEdit)}
                        >
                            CANCEL
                        </Button>
                    </div>
                    : ''
                }
            </div>
        );
    };

    onEditClicked = (itemId: string) => {
        this.setState(DEFAULT_EDIT_VALUES);
        this.setState({
            portalRef: document.getElementById(itemId),
            editId: itemId
        });
    };

    public render() {
        const {
            classes,
            comments,
            isLoading,
            isAdmin
        } = this.props;

        const { portalRef } = this.state;

        const commentsExist = !!comments && comments.length > 0;
        const commentsNumber = comments ? comments.length -1 : 0;

        return (
            <>
                <form className={classes.root}>
                    { this.renderTimeTrackerForm() }
                    { commentsExist && <Divider /> }
                    { comments &&
                        <>
                            {
                                comments.map((item: TimeTrackComment, index: number) => {
                                    const { id } = item;
                                    const isLastOne = commentsNumber === index;
                                    return (
                                        <div className={classes.logItemContainer} key={id}>
                                            <WorkLogItem
                                                comment={item}
                                            />
                                            <div id={id} className={`${classes.portalContainer}${isLastOne ? ' last-comment' : ''}`}></div>
                                            {
                                                isAdmin && (
                                                    <IconButton
                                                        className={classes.editButton}
                                                        size="small"
                                                        aria-label="Edit"
                                                        onClick={() => this.onEditClicked(id)}
                                                    >
                                                        <EditIcon fontSize="small"/>
                                                    </IconButton>
                                                )
                                            }
                                            { !isLastOne && <Divider /> }
                                        </div>
                                    );
                                })
                            }
                        </>
                    }
                </form>
                {
                    isAdmin && portalRef && (
                        <Portal container={portalRef}>
                            {
                                this.renderTimeTrackerForm(true)
                            }
                        </Portal>
                    )
                }
                <CCSpinner
                    loading={!!isLoading}
                    overlayVisible={true}
                />
            </>
        );
    }
}

const MUIComponent = withStyles(styles)(WorkLogEditor);
export { MUIComponent as WorkLogEditor };
